import * as React from 'react';
import { toast } from 'react-toastify';

import { BOTTOM_TOAST } from '../toastUtils';
import { petDog } from '../utils/petDog';

// Easter egg: type "treat" anywhere outside a text field and a treat drops from
// the top of the screen while a dog pops up from the bottom to catch it.
// Hover the dog and it waits; click to pet it. Decorative only: aria-hidden and
// never steals focus.

const SECRET_WORD = 'treat';
const MAX_KEY_GAP_MS = 1500; // typing pause that resets the sequence

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || !!target.closest('input, textarea, select, [contenteditable]');
};

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export const TreatDrop = () => {
  // Each drop gets a new id so typing "treat" again restarts the animation.
  const [drop, setDrop] = React.useState<{ id: number; x: number } | null>(null);
  const typedRef = React.useRef('');
  const lastKeyAtRef = React.useRef(0);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) return;
      if (event.key.length !== 1) return;

      const now = Date.now();
      if (now - lastKeyAtRef.current > MAX_KEY_GAP_MS) typedRef.current = '';
      lastKeyAtRef.current = now;
      typedRef.current = (typedRef.current + event.key.toLowerCase()).slice(-SECRET_WORD.length);

      if (typedRef.current !== SECRET_WORD) return;
      typedRef.current = '';

      if (prefersReducedMotion()) {
        toast('Good dog! 🦴', { ...BOTTOM_TOAST, toastId: 'bark-treat' });
        return;
      }
      // Keep the drop away from the edges so the dog head is fully visible.
      const x = window.innerWidth * (0.15 + Math.random() * 0.7);
      setDrop({ id: now, x });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!drop) return null;

  return (
    <div aria-hidden="true" className="fixed inset-0 z-[10000] pointer-events-none overflow-hidden">
      <div key={drop.id} className="treat-drop-lane" style={{ left: drop.x }}>
        <svg className="treat-drop-bone" width="36" height="16" viewBox="0 0 36 16">
          <path
            d="M7 3.2a3.6 3.6 0 0 0-3.4 4.8A3.6 3.6 0 1 0 8.4 12.8h19.2a3.6 3.6 0 1 0 4.8-4.8 3.6 3.6 0 1 0-4.8-4.8H8.4A3.6 3.6 0 0 0 7 3.2z"
            fill="#ecd5a6"
            stroke="#a9773f"
            strokeWidth="1.2"
          />
        </svg>
        <div
          className="treat-drop-dog"
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) setDrop(null);
          }}
        >
          <svg
            width="92"
            height="84"
            viewBox="0 0 92 84"
            onClick={(event) => petDog(event, event.currentTarget)}
          >
            {/* Ears */}
            <path d="M18 18 C4 22 2 44 10 52 C16 46 20 34 24 26 Z" fill="#8a5a2b" />
            <path d="M74 18 C88 22 90 44 82 52 C76 46 72 34 68 26 Z" fill="#8a5a2b" />
            {/* Head */}
            <ellipse cx="46" cy="44" rx="28" ry="30" fill="#c98d4b" />
            {/* Eyes, looking up at the treat */}
            <circle cx="35" cy="33" r="4" fill="#2b2420" />
            <circle cx="57" cy="33" r="4" fill="#2b2420" />
            <circle cx="36.2" cy="31.6" r="1.3" fill="#ffffff" />
            <circle cx="58.2" cy="31.6" r="1.3" fill="#ffffff" />
            {/* Mouth: open while waiting, snaps shut on the catch */}
            <g className="treat-drop-mouth">
              <ellipse cx="46" cy="62" rx="11" ry="8" fill="#6e2a2a" />
              <ellipse cx="46" cy="66" rx="6" ry="3.5" fill="#e46b7a" />
            </g>
            {/* Snout + nose */}
            <ellipse cx="46" cy="52" rx="14" ry="10" fill="#e8c79a" />
            <ellipse cx="46" cy="47" rx="5.5" ry="4" fill="#2b2420" />
          </svg>
          <span className="treat-drop-nom">nom!</span>
        </div>
      </div>
    </div>
  );
};
